import React from 'react';
import { Users, Globe, Zap, Shield, Code, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';

export const AboutUs = () => {
  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '80px'
    }}>
      <SEO 
        title="About Us" 
        description="Learn about Toolmetrico's mission to provide the world's fastest, most secure, and entirely client-side suite of everyday digital utilities for creators and developers."
        keywords="about toolmetrico, our mission, free digital tools, client-side tools, safe online utilities, developer tools, global web tools"
      />
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '32px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>About Us</span>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(5, 117, 251, 0.1)',
          color: 'var(--primary)',
          width: '64px',
          height: '64px',
          borderRadius: '20px',
          marginBottom: '20px'
        }}>
          <Globe size={32} />
        </div>
        <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', letterSpacing: '-0.02em' }}>
          About Toolmetrico
        </h1>
        <p style={{ fontSize: '1.2rem', color: 'var(--text-secondary)', maxWidth: '650px', margin: '0 auto', lineHeight: 1.6 }}>
          We are building the internet's fastest, most secure, and entirely client-side suite of everyday digital utilities. Designed for global creators, developers, and professionals.
        </p>
      </div>

      <div className="about-content-container">
        
        <section style={{ marginBottom: '48px' }}>
          <h2 className="about-heading">
            <Zap size={22} style={{ color: 'var(--primary)' }} /> Our Mission
          </h2>
          <p>
            In an increasingly complex digital landscape, fundamental tasks shouldn't require bloated software, expensive subscriptions, or compromising your personal data. Toolmetrico was founded on a simple premise: <strong>provide high-performance, beautiful, and free digital tools to anyone, anywhere in the world.</strong>
          </p>
          <p>
            Whether you are a student compressing a PDF for an assignment, a developer minifying code, or a small business generating inventory barcodes, our platform is engineered to accelerate your workflow without getting in your way.
          </p>
        </section>

        <section style={{ marginBottom: '48px' }}>
          <h2 className="about-heading">
            <Shield size={22} style={{ color: 'var(--primary)' }} /> The Privacy-First Difference
          </h2>
          <p>
            Most online toolkits process your files by uploading them to remote servers. This introduces massive security vulnerabilities, especially for sensitive corporate documents, personal photographs, or proprietary code.
          </p>
          <p>
            At Toolmetrico, we utilize a revolutionary <strong>100% Client-Side Processing</strong> architecture. When you compress an image, generate a secure password, or manipulate a PDF, the computational work happens entirely inside your own browser. Your files never touch our servers, ensuring absolute compliance with global privacy standards like GDPR and CCPA.
          </p>
        </section>

        <section style={{ marginBottom: '48px' }}>
          <h2 className="about-heading">
            <Code size={22} style={{ color: 'var(--primary)' }} /> Built for Performance
          </h2>
          <div style={{
            display: 'grid',
            gap: '24px',
            marginTop: '24px'
          }} className="about-features-grid">
            
            <div className="feature-card">
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>Zero Latency</h3>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>Because tools execute locally on your device, you never have to wait in a server queue or deal with slow upload/download speeds.</p>
            </div>

            <div className="feature-card">
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>Modern Tech Stack</h3>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>Built with cutting-edge web technologies, our tools leverage your device's native hardware acceleration for complex tasks like background removal and image compression.</p>
            </div>

            <div className="feature-card">
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>Responsive Design</h3>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>Every tool is meticulously crafted to work perfectly whether you are at a desktop workstation or executing tasks on your mobile phone on the go.</p>
            </div>

          </div>
        </section>

        <section>
          <h2 className="about-heading">
            <Users size={22} style={{ color: 'var(--primary)' }} /> Join Our Global Community
          </h2>
          <p>
            Toolmetrico is continuously evolving. We actively listen to feedback from our global user base to introduce new features, refine interfaces, and launch completely new tools that solve real-world problems. 
          </p>
          <p style={{ marginTop: '16px' }}>
            Thank you for trusting Toolmetrico as your go-to digital toolkit. We are excited to continue building tools that empower your creativity and productivity.
          </p>
        </section>
      </div>

      <style>{`
        .breadcrumb-back:hover {
          color: var(--primary) !important;
          transform: translateX(-2px);
        }
        
        .about-content-container {
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: 24px;
          padding: 48px;
          box-shadow: var(--shadow-sm);
          color: var(--text-primary);
          line-height: 1.8;
          font-size: 1.05rem;
        }

        .about-heading {
          font-size: 1.6rem;
          font-weight: 800;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .about-features-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .feature-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          padding: 24px;
          border-radius: 16px;
          transition: var(--transition-smooth);
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary);
        }

        @media (max-width: 991px) {
          .about-features-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 768px) {
          .about-content-container {
            padding: 32px 20px;
            border-radius: 16px;
            font-size: 1rem;
          }
          .about-heading {
            font-size: 1.35rem;
          }
          .about-features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
